import { useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import { Prescription } from '../../../types/models/Presc'
import { colorTokens } from '../../../shared/styles/theme'
import { RADIUS } from '../../../shared/styles/radii'
import { FONT_TABULAR } from '../../../shared/styles/typography'
import { ImagePane } from '../../medilab/latest/detail/image/ImagePane'
import { ItemsPane } from './ItemsPane'
import { FormatSettingPane } from './formatSetting/FormatSettingPane'

/**
 * 項目ペインの幅。顧客側 PrescriptionDetailPage と同値で持つ — 版面（保険が全幅、
 * その下を患者情報 / 医療機関の 2 列）が同じ幅で組まれることが条件なので、ここを動かさない。
 */
const ITEMS_PANE_WIDTH = 560

type TabKey = 'items' | 'format'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'items', label: '読取結果' },
  { key: 'format', label: 'フォーマット設定' },
]

/** Tab pill (02-components/tab-pill.md)。実 button で描き、active は bg.muted + fg.default */
const TabPill = ({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) => (
  <ButtonBase
    type="button"
    role="tab"
    aria-selected={isActive}
    onClick={onClick}
    sx={{
      height: 28,
      px: 1.25,
      borderRadius: `${RADIUS.md}px`,
      fontSize: 13,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      color: isActive ? colorTokens.fg.default : colorTokens.fg.muted,
      background: isActive ? colorTokens.bg.muted : 'transparent',
      transition: 'background-color 120ms',
      '&:hover': {
        background: isActive ? colorTokens.bg.muted : colorTokens.bg.emphasis,
      },
      '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
    }}
  >
    {label}
  </ButtonBase>
)

interface Props {
  prescription: Prescription
}

/**
 * 読取結果（詳細）。**画像左・項目右の左右分割**（ADR-0010 / ADR-0031）。
 *
 * 顧客側 PrescriptionDetailPage と同じ形だが、あちらは usePrescriptionsQuery を内で引いて
 * `.find()` する。こちらは処方箋を props で受け取るだけ（一覧・リクエストID の 2 経路から
 * 同じ形で渡ってくる）。**データ源を持たないことがこの面の隔絶**なので、ここで引かない。
 *
 * 右ペインは TabBar（pill variant）で「読取結果」と「フォーマット設定」を切り替える。
 * タブは紙を切り替えても保つ — 毎晩のチェックシートは紙を次々に開いて様式だけを見る仕事なので、
 * 紙ごとにタブへ戻されると手数が倍になる。
 *
 * **いま開いている紙の身元（リクエストID 全 36 文字）は tablist 行の右端に置く**（log/62 §2 A）。
 * 一覧から開いても ID 検索で開いても同じ場所に出るので、「欄が空でここに ID = 開いた」で Enter 済みかの
 * 迷いが消える。顧客側は DetailFooter（右下・サポートコード）に出すが、annotator にとって ID は
 * 読み上げる参照値でなく入口の鍵なので、スクロール前・タブを問わず見える位置に置く
 * （04-apps/mamoru.md annotator 節）。行は増やさない。`user-select: all` で 1 クリック全選択。
 */
export const DetailPane = ({ prescription }: Props) => {
  const [tab, setTab] = useState<TabKey>('items')

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          borderRight: `1px solid ${colorTokens.border.default}`,
        }}
      >
        <ImagePane
          images={prescription.presc_images ?? []}
          imageArea={
            prescription.basic_result?.処方箋存在領域?.selected_option ?? []
          }
        />
      </Box>
      <Box
        sx={{
          width: ITEMS_PANE_WIDTH,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          role="tablist"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            minHeight: 44,
            flexShrink: 0,
            borderBottom: `1px solid ${colorTokens.border.default}`,
          }}
        >
          {TABS.map(({ key, label }) => (
            <TabPill
              key={key}
              label={label}
              isActive={tab === key}
              onClick={() => setTab(key)}
            />
          ))}
          <Typography
            component="span"
            aria-label="開いているリクエストID"
            sx={{
              ml: 'auto',
              pl: 1,
              fontSize: 11,
              ...FONT_TABULAR,
              color: colorTokens.fg.muted,
              userSelect: 'all',
              whiteSpace: 'nowrap',
            }}
          >
            {prescription.ocr_request_event_id}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {tab === 'items' ? (
            // 紙ごとに state（店の旗を押したときの一言）を持ち直す
            <ItemsPane
              key={prescription.ocr_request_event_id}
              prescription={prescription}
            />
          ) : (
            <FormatSettingPane prescription={prescription} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
